import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Wand2, Loader2, Globe, Upload, Sparkles, QrCode, Share2, Copy,
  Facebook, Twitter, Linkedin, Mail, MessageSquare, Download,
  RefreshCw, ChevronRight, ImageIcon, Palette, Rocket, ArrowLeft,
  Camera, FileImage, CheckCircle2, PenLine, ExternalLink, Send,
  MessageCircle, MoreHorizontal, Instagram, BarChart3, Scissors,
  Zap, Smile, Type,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /ad-builder — AI Ad Builder
 *
 * 4-step flow:
 *   1. Intake (URL or manual "no-website" path)
 *   2. Review Brand + choose creative source (site photo / upload / AI)
 *   3. Pick a concept (Professional / Visual / Promotional)
 *   4. Publish → QR + SpLink + social share buttons
 */
export default function AdBuilder() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [project, setProject] = useState(null);
  const [busy, setBusy] = useState(false);

  // Step 1 — intake
  const [url, setUrl] = useState("");
  const [email, setEmail] = useState("");
  const [noWebsite, setNoWebsite] = useState(false);
  const [manual, setManual] = useState({
    business_name: "", title: "", description: "", offer: "",
    keywords: "", category: "", location: "", phone: "", ad_email: "",
    destination_url: "", cta: "", notes: "",
  });

  const readiness = project?.readiness;
  const concepts = project?.concepts || {};
  const renders = project?.renders || {};
  const published = project?.published;

  const refresh = async () => {
    if (!project?.id) return;
    try {
      const { data } = await axios.get(`${API}/ad-builder/projects/${project.id}`);
      setProject(data.project);
    } catch {}
  };

  // -----------------------------
  //  Step 1 — start
  // -----------------------------
  const start = async () => {
    if (!noWebsite && !url.trim()) { toast.error("Enter a website URL, or switch to no-website mode"); return; }
    if (!email.trim() || !/@/.test(email)) { toast.error("Enter your email"); return; }
    setBusy(true);
    try {
      const body = {
        email: email.trim(),
        ...(noWebsite ? {} : { url: url.trim() }),
        ...manual,
        keywords: manual.keywords ? manual.keywords.split(",").map(s => s.trim()).filter(Boolean) : undefined,
      };
      const { data } = await axios.post(`${API}/ad-builder/start`, body);
      setProject(data.project);
      setStep(2);
      toast.success("Business analyzed — review below");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to start");
    } finally { setBusy(false); }
  };

  // -----------------------------
  //  Step 2 — image & readiness
  // -----------------------------
  const upload = async (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      setBusy(true);
      try {
        await axios.post(`${API}/ad-builder/projects/${project.id}/upload-image`,
          { data_url: reader.result });
        await refresh();
        toast.success("Photo uploaded");
      } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
      finally { setBusy(false); }
    };
    reader.readAsDataURL(file);
  };

  const generateImage = async (style = "photographic") => {
    setBusy(true);
    try {
      await axios.post(`${API}/ad-builder/projects/${project.id}/generate-image`,
        { style });
      await refresh();
      toast.success("AI image generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Image generation failed");
    } finally { setBusy(false); }
  };

  const selectImage = async (imgId) => {
    setBusy(true);
    try {
      await axios.put(`${API}/ad-builder/projects/${project.id}`,
        { fields: { selected_image_id: imgId } });
      // manual patch doesn't affect selected_image_id — patch directly
      await axios.post(`${API}/ad-builder/projects/${project.id}/render`,
        { concept: "professional", ratios: [] }).catch(() => {});
      setProject(p => ({ ...p, selected_image_id: imgId }));
    } finally { setBusy(false); }
  };

  // -----------------------------
  //  Step 3 — concepts + render
  // -----------------------------
  const generateCopy = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/ad-builder/projects/${project.id}/generate-copy`, {});
      await refresh();
    } catch (e) { toast.error("Copy generation failed"); }
    finally { setBusy(false); }
  };

  const renderAll = async (concept) => {
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/ad-builder/projects/${project.id}/render`,
        { concept, ratios: ["1x1", "4x5", "9x16", "landscape"] });
      setProject(p => ({ ...p, renders: data.renders }));
      toast.success(`Rendered 4 formats for "${concept}"`);
      setStep(4);
    } catch (e) { toast.error("Render failed"); }
    finally { setBusy(false); }
  };

  // -----------------------------
  //  Step 4 — publish
  // -----------------------------
  const publish = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/ad-builder/projects/${project.id}/publish`,
        { show_qr: true, show_short_url: true });
      setProject(p => ({ ...p, published: data.published }));
      toast.success("Published! Short URL + QR ready");
    } catch (e) { toast.error(e?.response?.data?.detail || "Publish failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-fuchsia-950 text-white pb-20"
         data-testid="ad-builder">
      <div className="fixed inset-0 opacity-[0.03] pointer-events-none mix-blend-overlay"
           style={{ backgroundImage:
             "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='80' height='80'><filter id='n'><feTurbulence baseFrequency='0.9'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")" }} />

      <div className="relative max-w-6xl mx-auto px-6 pt-14">
        <button onClick={() => nav("/social-studio")}
                className="text-white/50 hover:text-white text-xs inline-flex items-center gap-1">
          <ArrowLeft className="w-3 h-3" /> Back to Social Studio
        </button>
        <div className="mt-3 text-[10px] uppercase tracking-[0.35em] text-fuchsia-400 font-bold flex items-center gap-3">
          WebsiteTech · AI Ad Builder
          <a href="/ad-builder/faq"
              data-testid="ad-faq-link"
              className="text-white/40 hover:text-white/90 normal-case tracking-normal text-xs underline">FAQ</a>
          <a href="/ad-builder/library"
              data-testid="ad-library-link"
              className="text-white/40 hover:text-white/90 normal-case tracking-normal text-xs underline">My ads</a>
        </div>
        <h1 className="mt-3 text-4xl sm:text-5xl font-bold leading-tight max-w-3xl">
          Turn any business into a
          {" "}<span className="bg-gradient-to-r from-fuchsia-400 via-orange-300 to-amber-300 bg-clip-text text-transparent">
            professional ad
          </span> in 60 seconds
        </h1>
        <p className="mt-4 text-white/70 text-lg max-w-2xl">
          Enter a website — or just a title and description if you have no site — and we'll
          generate copy, imagery, formats, short URL, and QR code.
        </p>

        {/* Step bar */}
        <div className="mt-8 flex items-center gap-2 flex-wrap" data-testid="ad-builder-stepbar">
          {[
            [1, "Intake", Globe],
            [2, "Assets", Palette],
            [3, "Concepts", Sparkles],
            [4, "Publish", Rocket],
          ].map(([n, lbl, Icon]) => (
            <div key={n} className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-bold ${
                step === n ? "bg-white text-slate-900"
                 : step > n ? "bg-emerald-500/30 text-emerald-200"
                 : "bg-white/10 text-white/60"}`}
                 data-testid={`ad-step-${n}`}>
              {step > n ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />}
              {n}. {lbl}
            </div>
          ))}
        </div>

        {/* STEP 1 — Intake */}
        {step === 1 && (
          <div className="mt-8 rounded-3xl bg-white/5 ring-1 ring-white/10 backdrop-blur p-6 max-w-3xl">
            <div className="flex items-center gap-2 mb-4">
              <label className="text-[10px] uppercase tracking-widest text-white/50 font-bold">Mode</label>
              <div className="inline-flex rounded-lg bg-white/10 p-0.5">
                <button onClick={() => setNoWebsite(false)}
                         data-testid="ad-mode-url"
                         className={`px-3 py-1 text-xs font-bold rounded-md ${!noWebsite ? "bg-fuchsia-500 text-white" : "text-white/60"}`}>
                  I have a website
                </button>
                <button onClick={() => setNoWebsite(true)}
                         data-testid="ad-mode-nosite"
                         className={`px-3 py-1 text-xs font-bold rounded-md ${noWebsite ? "bg-fuchsia-500 text-white" : "text-white/60"}`}>
                  No website
                </button>
              </div>
            </div>

            {!noWebsite && (
              <Field icon={Globe} label="Website URL *" value={url} onChange={setUrl}
                      placeholder="indiatourism.pro" testid="ad-url" />
            )}
            <Field icon={Mail} label="Your email *" value={email} onChange={setEmail}
                    placeholder="you@company.com" testid="ad-email" />

            {noWebsite && (
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Business name" value={manual.business_name}
                        onChange={(v) => setManual(m => ({...m, business_name: v}))}
                        placeholder="Evergreen Landscaping" testid="ad-biz-name" />
                <Field label="Category / What you do" value={manual.category}
                        onChange={(v) => setManual(m => ({...m, category: v}))}
                        placeholder="Landscaping" testid="ad-category" />
                <Field label="Title / Headline" value={manual.title}
                        onChange={(v) => setManual(m => ({...m, title: v}))}
                        placeholder="Beautiful lawns, guaranteed" testid="ad-title" />
                <Field label="Location" value={manual.location}
                        onChange={(v) => setManual(m => ({...m, location: v}))}
                        placeholder="Portland, OR" testid="ad-location" />
                <div className="sm:col-span-2">
                  <Field textarea label="Short description" value={manual.description}
                          onChange={(v) => setManual(m => ({...m, description: v}))}
                          placeholder="Residential landscaping and garden maintenance"
                          testid="ad-description" />
                </div>
                <Field label="Keywords (comma-separated)" value={manual.keywords}
                        onChange={(v) => setManual(m => ({...m, keywords: v}))}
                        placeholder="lawn, pruning, planting" testid="ad-keywords" />
                <Field label="Offer / Promo (optional)" value={manual.offer}
                        onChange={(v) => setManual(m => ({...m, offer: v}))}
                        placeholder="20% off first month" testid="ad-offer" />
                <Field label="Phone (optional)" value={manual.phone}
                        onChange={(v) => setManual(m => ({...m, phone: v}))}
                        placeholder="+1 555 000 0000" testid="ad-phone" />
                <Field label="Destination URL / Landing" value={manual.destination_url}
                        onChange={(v) => setManual(m => ({...m, destination_url: v}))}
                        placeholder="https://your-page" testid="ad-dest" />
                <Field label="Preferred CTA" value={manual.cta}
                        onChange={(v) => setManual(m => ({...m, cta: v}))}
                        placeholder="Call today" testid="ad-cta" />
              </div>
            )}

            <button onClick={start} disabled={busy}
                     data-testid="ad-start-btn"
                     className="mt-6 rounded-xl bg-gradient-to-r from-fuchsia-500 to-orange-400 hover:brightness-110 text-white px-6 py-3 font-bold inline-flex items-center gap-2 disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
              Analyze & build my ad
            </button>
            <div className="mt-3 text-xs text-white/50">
              Free — 3 drafts / email / day. No credit card.
            </div>
          </div>
        )}

        {/* STEP 2 — Assets */}
        {step === 2 && project && (
          <AssetsStep project={project} refresh={refresh} onNext={() => setStep(3)}
                       upload={upload} generateImage={generateImage} busy={busy}
                       selectImage={selectImage} />
        )}

        {/* STEP 3 — Concepts */}
        {step === 3 && project && (
          <ConceptsStep project={project} generateCopy={generateCopy}
                          renderAll={renderAll} busy={busy} onBack={() => setStep(2)} />
        )}

        {/* STEP 4 — Publish */}
        {step === 4 && project && (
          <PublishStep project={project} publish={publish} busy={busy}
                        onBack={() => setStep(3)} />
        )}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
//  Sub-components
// ---------------------------------------------------------------------------
function Field({ icon: Icon, label, value, onChange, placeholder, textarea, testid }) {
  const El = textarea ? "textarea" : "input";
  return (
    <label className="block mb-3">
      <span className="text-[10px] uppercase tracking-widest text-white/50 font-bold mb-1 block">
        {label}
      </span>
      <div className="relative">
        {Icon && <Icon className="w-4 h-4 absolute left-3 top-3.5 text-white/40" />}
        <El value={value} onChange={(e) => onChange(e.target.value)}
             placeholder={placeholder} rows={textarea ? 3 : undefined}
             data-testid={testid}
             className={`w-full rounded-xl bg-white/10 ring-1 ring-white/10 ${Icon ? "pl-9" : "pl-3"} pr-3 py-3 text-sm placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-fuchsia-400`} />
      </div>
    </label>
  );
}


function AssetsStep({ project, refresh, onNext, upload, generateImage, busy, selectImage }) {
  const fileInputRef = useRef(null);
  const brand = project.brand || {};
  const manual = project.manual || {};
  const readiness = project.readiness || {};
  const allImages = [
    ...(project.image_candidates || []).map(c => ({ ...c, source: "site" })),
    ...(project.uploaded_images || []).map(c => ({ ...c, source: "uploaded" })),
    ...(project.generated_images || []).map(c => ({ ...c, source: "generated" })),
  ];
  return (
    <div className="mt-8 space-y-6" data-testid="ad-step2">
      <div className="rounded-3xl bg-white/5 ring-1 ring-white/10 backdrop-blur p-6"
           data-testid="ad-readiness">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold">
              Ad readiness
            </div>
            <div className="text-3xl font-bold">{readiness.score || 0}
              <span className="text-lg text-white/50 font-normal"> / 100</span>
              <span className={`ml-3 text-sm rounded-full px-3 py-1 ${
                readiness.band === "ready" ? "bg-emerald-500/30 text-emerald-200"
                 : readiness.band === "almost" ? "bg-amber-500/30 text-amber-200"
                 : "bg-rose-500/30 text-rose-200"}`}>
                {readiness.band}
              </span>
            </div>
          </div>
          <button onClick={onNext} disabled={busy}
                  data-testid="ad-to-concepts"
                  className="rounded-xl bg-white text-slate-900 hover:bg-white/90 px-5 py-3 font-bold inline-flex items-center gap-2">
            Continue to concepts <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        {(readiness.missing || []).length > 0 && (
          <div className="mt-4 text-xs text-white/60">
            Missing: {readiness.missing.map(m => (
              <span key={m} className="inline-block bg-white/10 rounded-full px-2 py-0.5 mr-1 mb-1">{m}</span>
            ))}
          </div>
        )}
      </div>

      {/* Discovered business info */}
      <div className="rounded-3xl bg-white/5 ring-1 ring-white/10 backdrop-blur p-6">
        <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-bold mb-3">
          We understood
        </div>
        <div className="grid sm:grid-cols-2 gap-3 text-sm">
          <InfoLine label="Business" value={brand?.business_name?.value || manual.business_name} />
          <InfoLine label="Category" value={brand?.business_type?.value || manual.category} />
          <InfoLine label="Tagline" value={brand?.tagline?.value || manual.title} />
          <InfoLine label="Location" value={manual.location} />
          <InfoLine label="Description" value={brand?.one_liner?.value || manual.description}
                     wide />
          <InfoLine label="Services" value={(brand?.services?.value || []).join(", ")} wide />
        </div>
      </div>

      {/* Image chooser */}
      <div className="rounded-3xl bg-white/5 ring-1 ring-white/10 backdrop-blur p-6"
           data-testid="ad-images">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-bold">
            Choose your image
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => fileInputRef.current?.click()}
                    disabled={busy}
                    data-testid="ad-upload-btn"
                    className="rounded-lg bg-white/10 hover:bg-white/20 px-3 py-2 text-xs font-bold inline-flex items-center gap-1">
              <Upload className="w-3.5 h-3.5" /> Upload photo
            </button>
            <input ref={fileInputRef} type="file" accept="image/*"
                    onChange={(e) => upload(e.target.files?.[0])}
                    className="hidden" data-testid="ad-upload-input" />
            <button onClick={() => generateImage("photographic")}
                    disabled={busy}
                    data-testid="ad-gen-photo-btn"
                    className="rounded-lg bg-fuchsia-500/30 hover:bg-fuchsia-500/50 px-3 py-2 text-xs font-bold inline-flex items-center gap-1">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              AI photograph
            </button>
            <button onClick={() => generateImage("illustration")}
                    disabled={busy}
                    data-testid="ad-gen-illust-btn"
                    className="rounded-lg bg-fuchsia-500/30 hover:bg-fuchsia-500/50 px-3 py-2 text-xs font-bold inline-flex items-center gap-1">
              <Palette className="w-3.5 h-3.5" /> AI illustration
            </button>
          </div>
        </div>

        {allImages.length === 0 ? (
          <div className="rounded-xl border-2 border-dashed border-white/20 p-8 text-center">
            <ImageIcon className="w-8 h-8 mx-auto text-white/30" />
            <div className="mt-2 text-white/60 text-sm">No images yet — upload one or generate with AI</div>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {allImages.map((img, i) => {
              const isSelected = project.selected_image_id === img.id;
              const fullUrl = img.url?.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${img.url}` : img.src;
              return (
                <button key={img.id || i} onClick={() => selectImage(img.id)}
                        data-testid={`ad-img-${img.kind}-${i}`}
                        className={`relative aspect-square rounded-xl overflow-hidden ring-2 ${
                          isSelected ? "ring-fuchsia-400" : "ring-white/10 hover:ring-white/40"}`}>
                  <img src={fullUrl} alt="" className="w-full h-full object-cover"
                        onError={(e) => e.currentTarget.style.opacity = 0.3} />
                  <span className="absolute top-1 left-1 text-[9px] font-bold uppercase tracking-widest bg-black/60 text-white px-1.5 py-0.5 rounded">
                    {img.source}
                  </span>
                  {isSelected && (
                    <span className="absolute top-1 right-1 bg-fuchsia-500 text-white rounded-full w-5 h-5 flex items-center justify-center">
                      <CheckCircle2 className="w-3 h-3" />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}


function ConceptsStep({ project, generateCopy, renderAll, busy, onBack }) {
  const concepts = project.concepts || {};
  const hasConcepts = Object.keys(concepts).length > 0;

  useEffect(() => {
    if (!hasConcepts && !busy) generateCopy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="mt-8 space-y-6" data-testid="ad-step3">
      <div className="flex items-center gap-3 mb-2">
        <button onClick={onBack} className="text-white/60 hover:text-white text-xs inline-flex items-center gap-1">
          <ArrowLeft className="w-3 h-3" /> Back
        </button>
        <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-bold">
          Pick a creative direction
        </div>
      </div>

      {!hasConcepts && (
        <div className="rounded-3xl bg-white/5 ring-1 ring-white/10 backdrop-blur p-8 text-center">
          <Loader2 className="w-6 h-6 mx-auto animate-spin text-fuchsia-300" />
          <div className="mt-2 text-white/70">Writing 3 ad concepts…</div>
        </div>
      )}

      {hasConcepts && (
        <div className="grid md:grid-cols-3 gap-4">
          {["professional", "visual", "promotional"].map((k) => {
            const c = concepts[k] || {};
            return (
              <div key={k} className="rounded-2xl bg-white/5 ring-1 ring-white/10 backdrop-blur p-5 flex flex-col"
                    data-testid={`ad-concept-${k}`}>
                <div className="text-[10px] uppercase tracking-widest font-bold text-fuchsia-300">
                  {k}
                </div>
                <h3 className="mt-2 text-xl font-bold">{c.headline}</h3>
                {c.subheadline && <div className="text-sm text-white/80">{c.subheadline}</div>}
                <p className="mt-3 text-sm text-white/70 flex-1">{c.short_description}</p>
                {c.offer_line && (
                  <div className="mt-3 text-xs bg-amber-500/20 text-amber-200 rounded-full px-3 py-1 inline-block w-fit">
                    {c.offer_line}
                  </div>
                )}
                {/* Surgical edit chips */}
                <SurgicalEdits pid={project.id} concept={k}
                                 hasOffer={!!c.offer_line}
                                 onUpdated={(newC) => {
                                   project.concepts[k] = newC;
                                 }} />
                <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between gap-2">
                  <div className="text-xs font-bold uppercase tracking-widest text-white/80">
                    {c.cta}
                  </div>
                  <button onClick={() => renderAll(k)} disabled={busy}
                           data-testid={`ad-render-${k}`}
                           className="rounded-lg bg-fuchsia-500 hover:bg-fuchsia-400 text-white px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1">
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                    Use this
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {hasConcepts && (
        <button onClick={generateCopy} disabled={busy}
                data-testid="ad-regen-copy"
                className="rounded-lg bg-white/10 hover:bg-white/20 px-3 py-2 text-xs font-bold inline-flex items-center gap-1">
          <RefreshCw className="w-3.5 h-3.5" /> Regenerate all concepts
        </button>
      )}
    </div>
  );
}


function PublishStep({ project, publish, busy, onBack }) {
  const renders = project.renders || {};
  const published = project.published;
  const [copied, setCopied] = useState("");
  const backend = process.env.REACT_APP_BACKEND_URL;

  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(""), 1500);
    toast.success("Copied");
  };

  return (
    <div className="mt-8 space-y-6" data-testid="ad-step4">
      <div className="flex items-center gap-3 mb-2">
        <button onClick={onBack} className="text-white/60 hover:text-white text-xs inline-flex items-center gap-1">
          <ArrowLeft className="w-3 h-3" /> Back
        </button>
      </div>

      {/* Renders */}
      <div className="rounded-3xl bg-white/5 ring-1 ring-white/10 backdrop-blur p-6">
        <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-bold mb-4">
          Your ad in 4 formats
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {["1x1", "4x5", "9x16", "landscape"].map((r) => {
            const rd = renders[r];
            return (
              <div key={r} className="rounded-xl bg-white/5 ring-1 ring-white/10 p-2 flex flex-col"
                    data-testid={`ad-render-preview-${r}`}>
                <div className="aspect-square bg-black/30 rounded-lg overflow-hidden flex items-center justify-center">
                  {rd ? (
                    <img src={`${backend}${rd.url}`} alt={r}
                          className="max-w-full max-h-full object-contain" />
                  ) : (
                    <span className="text-white/40 text-xs">not rendered</span>
                  )}
                </div>
                <div className="mt-2 flex items-center justify-between text-xs">
                  <span className="text-white/60 font-mono">{r}</span>
                  {rd && (
                    <a href={`${backend}${rd.url}`} download={`ad-${r}.png`}
                        data-testid={`ad-download-${r}`}
                        className="text-fuchsia-300 hover:text-fuchsia-200 inline-flex items-center gap-1">
                      <Download className="w-3 h-3" /> PNG
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Publish CTA */}
      {!published && (
        <button onClick={publish} disabled={busy}
                data-testid="ad-publish-btn"
                className="w-full rounded-2xl bg-gradient-to-r from-fuchsia-500 to-orange-400 hover:brightness-110 text-white p-6 font-bold inline-flex items-center justify-center gap-3 text-lg disabled:opacity-50">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Rocket className="w-5 h-5" />}
          Publish — mint short URL + QR + share links
        </button>
      )}

      {published && (
        <div className="rounded-3xl bg-gradient-to-br from-emerald-500/10 via-white/5 to-fuchsia-500/10 ring-1 ring-emerald-400/30 backdrop-blur p-6"
             data-testid="ad-published">
          <div className="flex items-center gap-2 text-emerald-300 font-bold uppercase tracking-widest text-[10px]">
            <CheckCircle2 className="w-4 h-4" /> Live
          </div>
          <div className="mt-4 grid md:grid-cols-[1fr_180px] gap-6 items-center">
            <div className="space-y-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-white/50 font-bold">Short URL</div>
                <div className="mt-1 flex items-center gap-2">
                  <a href={published.short_url} target="_blank" rel="noreferrer"
                      className="text-xl font-mono text-fuchsia-300 hover:text-fuchsia-200 inline-flex items-center gap-1 break-all"
                      data-testid="ad-short-url">
                    {published.short_url} <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                  <button onClick={() => copy(published.short_url, "url")}
                          data-testid="ad-copy-short-url"
                          className="text-white/60 hover:text-white">
                    <Copy className="w-4 h-4" />
                  </button>
                  {copied === "url" && <span className="text-emerald-300 text-xs">copied!</span>}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-white/50 font-bold">Suggested caption</div>
                <div className="mt-1 flex items-start gap-2">
                  <div className="flex-1 text-sm bg-white/10 rounded-lg p-3">{published.caption}</div>
                  <button onClick={() => copy(published.caption, "cap")}
                          data-testid="ad-copy-caption"
                          className="text-white/60 hover:text-white mt-3">
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
                {copied === "cap" && <span className="text-emerald-300 text-xs">copied!</span>}
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-white/50 font-bold mb-2">Share one-tap</div>
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                  <ShareBtn href={published.share_links.whatsapp} label="WhatsApp"
                             icon={MessageSquare}
                             color="bg-emerald-500 hover:bg-emerald-400" testid="ad-share-wa" />
                  <ShareBtn href={published.share_links.telegram} label="Telegram"
                             icon={Send}
                             color="bg-sky-500 hover:bg-sky-400" testid="ad-share-tg" />
                  <ShareBtn href={published.share_links.x} label="X"
                             icon={Twitter}
                             color="bg-slate-800 hover:bg-slate-700" testid="ad-share-x" />
                  <ShareBtn href={published.share_links.facebook} label="Facebook"
                             icon={Facebook}
                             color="bg-blue-600 hover:bg-blue-500" testid="ad-share-fb" />
                  <ShareBtn href={published.share_links.linkedin} label="LinkedIn"
                             icon={Linkedin}
                             color="bg-sky-700 hover:bg-sky-600" testid="ad-share-li" />
                  <ShareBtn href={published.share_links.reddit} label="Reddit"
                             icon={MessageCircle}
                             color="bg-orange-500 hover:bg-orange-400" testid="ad-share-reddit" />
                  <ShareBtn href={published.share_links.threads} label="Threads"
                             icon={MessageCircle}
                             color="bg-slate-900 hover:bg-slate-700" testid="ad-share-threads" />
                  <ShareBtn href={published.share_links.pinterest} label="Pinterest"
                             icon={ImageIcon}
                             color="bg-red-600 hover:bg-red-500" testid="ad-share-pin" />
                  <ShareBtn href={published.share_links.email} label="Email"
                             icon={Mail}
                             color="bg-slate-600 hover:bg-slate-500" testid="ad-share-email" />
                  <ShareBtn href={published.share_links.sms} label="SMS"
                             icon={MessageSquare}
                             color="bg-emerald-600 hover:bg-emerald-500" testid="ad-share-sms" />
                  <button onClick={() => {
                            copy(`${published.caption}\n\n${published.short_url}`, "ig");
                          }}
                          data-testid="ad-share-instagram"
                          className="inline-flex items-center gap-1 rounded-lg bg-gradient-to-tr from-fuchsia-500 via-orange-400 to-amber-300 text-white px-3 py-2 text-xs font-bold flex-col justify-center">
                    <Instagram className="w-3.5 h-3.5" /> Instagram
                  </button>
                  <button onClick={async () => {
                            if (navigator.share) {
                              try {
                                await navigator.share({title: "Check this out",
                                    text: published.caption, url: published.short_url});
                              } catch {}
                            } else copy(published.short_url, "more");
                          }}
                          data-testid="ad-share-more"
                          className="inline-flex flex-col items-center justify-center gap-1 rounded-lg bg-white/10 hover:bg-white/20 text-white px-3 py-2 text-xs font-bold">
                    <MoreHorizontal className="w-3.5 h-3.5" /> More
                  </button>
                </div>
                {copied === "ig" && (
                  <div className="mt-2 text-xs text-emerald-300 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Caption + link copied — download the 1:1 image below and paste in Instagram
                  </div>
                )}
              </div>
              {/* Analytics quick tab */}
              <AnalyticsTab pid={project.id} shortUrl={published.short_url} />
            </div>
            <div className="rounded-xl bg-white p-3">
              <img src={`${backend}/api/ad-builder/projects/${project.id}/qr.png?size=512`}
                    alt="QR code"
                    data-testid="ad-qr-img"
                    className="w-full aspect-square object-contain" />
              <a href={`${backend}/api/ad-builder/projects/${project.id}/qr.png?size=1024`}
                  download={`qr-${project.id.slice(0,8)}.png`}
                  data-testid="ad-qr-download"
                  className="mt-2 block text-center text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1">
                <Download className="w-3 h-3" /> Download QR
              </a>
            </div>
          </div>

          {/* Print pack + Approval sharing */}
          <div className="mt-6 pt-6 border-t border-white/10 grid sm:grid-cols-2 gap-3">
            <PrintPackBtn pid={project.id} />
            <ApprovalShareBtn pid={project.id} approval={project.approval} />
          </div>
        </div>
      )}
    </div>
  );
}


function PrintPackBtn({ pid }) {
  const [busy, setBusy] = useState(false);
  const backend = process.env.REACT_APP_BACKEND_URL;
  const download = async () => {
    setBusy(true);
    try {
      const res = await axios.post(
        `${backend}/api/ad-builder/projects/${pid}/print-pack`,
        { concept: "promotional" },
        { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/zip" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `print-pack-${pid.slice(0, 8)}.zip`;
      link.click();
      toast.success("Print pack downloaded — 5×7 flyer + business card + poster");
    } catch (e) { toast.error("Print pack failed"); }
    finally { setBusy(false); }
  };
  return (
    <button onClick={download} disabled={busy}
             data-testid="ad-print-pack-btn"
             className="rounded-xl bg-white/10 hover:bg-white/20 text-white px-4 py-3 text-sm font-bold inline-flex items-center justify-center gap-2">
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
      Print pack (5×7 flyer + card + poster)
    </button>
  );
}


function ApprovalShareBtn({ pid, approval }) {
  const [busy, setBusy] = useState(false);
  const [token, setToken] = useState(approval?.token || "");
  const [copied, setCopied] = useState(false);
  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/ad-builder/projects/${pid}/share-approval`, {});
      setToken(data.token);
      toast.success("Approval link ready");
    } catch { toast.error("Failed"); }
    finally { setBusy(false); }
  };
  const link = token ? `${window.location.origin}/ad-approval/${token}` : "";
  const copy = () => {
    navigator.clipboard.writeText(link);
    setCopied(true); setTimeout(() => setCopied(false), 1500);
    toast.success("Link copied");
  };
  if (!token) {
    return (
      <button onClick={generate} disabled={busy}
               data-testid="ad-approval-share-btn"
               className="rounded-xl bg-white/10 hover:bg-white/20 text-white px-4 py-3 text-sm font-bold inline-flex items-center justify-center gap-2">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
        Share for approval
      </button>
    );
  }
  return (
    <div className="rounded-xl bg-emerald-500/10 ring-1 ring-emerald-400/30 px-4 py-3"
         data-testid="ad-approval-share-active">
      <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold flex items-center gap-1">
        <CheckCircle2 className="w-3 h-3" /> Approval link ready
      </div>
      <div className="mt-1 flex items-center gap-2 text-xs">
        <span className="font-mono text-white/80 truncate flex-1">{link}</span>
        <button onClick={copy}
                data-testid="ad-approval-copy"
                className="text-white/60 hover:text-white">
          <Copy className="w-3.5 h-3.5" />
        </button>
        {copied && <span className="text-emerald-300 text-xs">copied!</span>}
      </div>
    </div>
  );
}


function InfoLine({ label, value, wide }) {
  return (
    <div className={wide ? "sm:col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-widest text-white/50 font-bold">{label}</div>
      <div className="text-white/90 truncate">{value || <span className="text-white/30 italic">—</span>}</div>
    </div>
  );
}

function SurgicalEdits({ pid, concept, hasOffer, onUpdated }) {
  const [busy, setBusy] = useState("");
  const actions = [
    { key: "rewrite_headline",  label: "Rewrite headline",  icon: PenLine },
    { key: "shorten",            label: "Shorten",           icon: Scissors },
    { key: "punchier",           label: "Punchier",          icon: Zap },
    { key: "more_friendly",      label: "Friendlier",        icon: Smile },
    { key: "change_cta",         label: "Change CTA",        icon: Type },
    hasOffer
      ? { key: "remove_offer",    label: "Remove offer",     icon: Scissors }
      : { key: "add_offer",       label: "Add offer",        icon: Sparkles },
    { key: "regenerate",         label: "Regenerate",        icon: RefreshCw },
  ];
  const run = async (action) => {
    setBusy(action);
    try {
      const { data } = await axios.post(
        `${API}/ad-builder/projects/${pid}/edit-concept`,
        { concept, action });
      onUpdated(data.concept);
      toast.success("Updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Edit failed"); }
    finally { setBusy(""); }
  };
  return (
    <div className="mt-3 flex flex-wrap gap-1" data-testid={`ad-surgical-${concept}`}>
      {actions.map(a => (
        <button key={a.key} onClick={() => run(a.key)} disabled={!!busy}
                 data-testid={`ad-edit-${concept}-${a.key}`}
                 className="inline-flex items-center gap-1 rounded-full bg-white/10 hover:bg-white/20 text-white/80 hover:text-white px-2 py-1 text-[10px] font-bold disabled:opacity-50">
          {busy === a.key ? <Loader2 className="w-3 h-3 animate-spin" /> : <a.icon className="w-3 h-3" />}
          {a.label}
        </button>
      ))}
    </div>
  );
}


function ShareBtn({ href, label, icon: Icon, color, testid }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" data-testid={testid}
        className={`inline-flex flex-col items-center justify-center gap-1 rounded-lg ${color} text-white px-3 py-2 text-xs font-bold`}>
      <Icon className="w-3.5 h-3.5" /> {label}
    </a>
  );
}


function AnalyticsTab({ pid, shortUrl }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data: r } = await axios.get(`${API}/ad-builder/projects/${pid}/analytics`);
      setData(r);
    } catch { setData({ error: "failed" }); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl bg-white/5 ring-1 ring-white/10 p-4" data-testid="ad-analytics">
      <button onClick={() => { setOpen(o => !o); if (!open && !data) load(); }}
              data-testid="ad-analytics-toggle"
              className="w-full flex items-center justify-between text-left">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-fuchsia-300" />
          <span className="text-[10px] uppercase tracking-widest text-white/50 font-bold">
            Campaign performance
          </span>
        </div>
        <ChevronRight className={`w-4 h-4 text-white/50 transition-transform ${open ? "rotate-90" : ""}`} />
      </button>
      {open && (
        <div className="mt-3">
          {busy && <div className="flex items-center gap-2 text-white/60 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>}
          {data && !busy && (
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg bg-white/5 p-3">
                <div className="text-[10px] uppercase tracking-widest text-white/50 font-bold">Total clicks</div>
                <div className="text-3xl font-extrabold text-fuchsia-300"
                     data-testid="ad-analytics-clicks">
                  {data.clicks_total ?? 0}
                </div>
              </div>
              <div className="rounded-lg bg-white/5 p-3">
                <div className="text-[10px] uppercase tracking-widest text-white/50 font-bold">Short URL</div>
                <div className="text-sm text-white/80 font-mono break-all">
                  {data.short_url || shortUrl}
                </div>
              </div>
              {data.error && (
                <div className="col-span-2 text-xs text-amber-300">{data.error}</div>
              )}
              {!data.configured && (
                <div className="col-span-2 text-xs text-white/50">
                  Analytics upstream not configured — clicks won't be recorded.
                </div>
              )}
            </div>
          )}
          <button onClick={load} disabled={busy}
                  data-testid="ad-analytics-refresh"
                  className="mt-3 rounded-lg bg-white/10 hover:bg-white/20 px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1">
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>
      )}
    </div>
  );
}
